import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import DepartmentList from '../features/departments/DepartmentList';
import DepartmentForm from '../features/departments/DepartmentForm';
import { useNotification } from '../context/NotificationContext';
import departmentService from '../services/departmentService';

const Departments = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [viewingDepartment, setViewingDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess } = useNotification();

  const handleCreate = () => {
    setEditingDepartment(null);
    setShowForm(true);
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setShowForm(true);
  };

  const handleView = (department) => {
    setViewingDepartment(department);
  };

  const handleCloseView = () => {
    setViewingDepartment(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingDepartment) {
        response = await departmentService.updateDepartment(editingDepartment.id, formData);
        if (response.success) {
          showSuccess('Department updated successfully!');
        }
      } else {
        response = await departmentService.createDepartment(formData);
        if (response.success) {
          showSuccess('Department created successfully!');
        }
      }

      setShowForm(false);
      setEditingDepartment(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingDepartment(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MainLayout>
      <div className="departments-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">Department Management</h1>
            <p className="text-muted mb-0">Manage academic departments within faculties</p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={handleCreate}
          >
            <i className="fas fa-plus me-2"></i>
            Add Department
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className={`fas ${editingDepartment ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                    {editingDepartment ? 'Edit Department' : 'Add New Department'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <DepartmentForm
                    department={editingDepartment}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewingDepartment && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-sitemap me-2"></i>
                    Department Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseView}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      {/* Department Header */}
                      <div className="d-flex align-items-center mb-4">
                        <div 
                          className={`department-icon rounded-circle d-flex align-items-center justify-content-center me-3 ${
                            viewingDepartment.status ? 'bg-primary' : 'bg-secondary'
                          }`}
                          style={{ width: '70px', height: '70px' }}
                        >
                          <i className="fas fa-sitemap text-white fs-3"></i>
                        </div>
                        <div>
                          <h3 className="fw-bold text-dark mb-1">{viewingDepartment.nameEn}</h3>
                          <p className="text-muted mb-2 fs-5">{viewingDepartment.nameLocal}</p>
                          <div className="d-flex gap-2 flex-wrap">
                            <span className="badge bg-info fs-6">
                              <i className="fas fa-graduation-cap me-1"></i>
                              {viewingDepartment.facultyName}
                            </span>
                            <span className="badge bg-primary fs-6">
                              <i className="fas fa-building me-1"></i>
                              {viewingDepartment.campusName}
                            </span>
                            <span 
                              className={`badge fs-6 ${viewingDepartment.status ? 'bg-success' : 'bg-danger'}`}
                            >
                              <i className={`fas ${viewingDepartment.status ? 'fa-check-circle' : 'fa-times-circle'} me-1`}></i>
                              {viewingDepartment.status ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hierarchy Information */}
                      <div className="card border-0 bg-light mb-4">
                        <div className="card-body">
                          <h6 className="fw-bold mb-3">
                            <i className="fas fa-sitemap me-2 text-primary"></i>
                            Academic Hierarchy
                          </h6>
                          <div className="department-hierarchy">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-building text-primary me-2"></i>
                              <span className="fw-medium">Campus:</span>
                              <span className="ms-2">{viewingDepartment.campusName}</span>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-graduation-cap text-info me-2"></i>
                              <span className="fw-medium">Faculty:</span>
                              <span className="ms-2">{viewingDepartment.facultyName}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <i className="fas fa-sitemap text-success me-2"></i>
                              <span className="fw-medium">Department:</span>
                              <span className="ms-2">{viewingDepartment.nameEn}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Basic Information */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">DEPARTMENT ID</label>
                          <p className="text-dark fs-5">{viewingDepartment.id}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">CAMPUS ID</label>
                          <p className="text-dark fs-5">{viewingDepartment.campusId}</p>
                        </div>
                      </div>

                      {/* Names */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">ENGLISH NAME</label>
                          <p className="text-dark">{viewingDepartment.nameEn}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">LOCAL NAME</label>
                          <p className="text-dark">{viewingDepartment.nameLocal}</p>
                        </div>
                      </div>

                      {/* Faculty Information */}
                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">FACULTY ID</label>
                          <p className="text-dark">{viewingDepartment.facultyId}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">FACULTY NAME</label>
                          <p className="text-dark">{viewingDepartment.facultyName}</p>
                        </div>
                      </div>

                      {/* Timestamps */}
                      <div className="row">
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">CREATED</label>
                          <p className="text-dark">{formatDate(viewingDepartment.createdAt)}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold text-muted small">LAST UPDATED</label>
                          <p className="text-dark">{formatDate(viewingDepartment.updatedAt)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Statistics Panel */}
                    <div className="col-md-4">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body">
                          <h6 className="fw-bold mb-4">
                            <i className="fas fa-chart-bar me-2"></i>
                            Department Statistics
                          </h6>
                          
                          {/* Students */}
                          <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                 style={{ width: '55px', height: '55px' }}>
                              <i className="fas fa-user-graduate text-white fs-4"></i>
                            </div>
                            <div>
                              <div className="fw-bold fs-2">{viewingDepartment.studentCount || 0}</div>
                              <small className="text-muted">Enrolled Students</small>
                            </div>
                          </div>

                          {/* Department Status Card */}
                          <div className="card border-0 bg-white mb-4">
                            <div className="card-body text-center py-3">
                              <div 
                                className={`${viewingDepartment.status ? 'bg-success' : 'bg-danger'} rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                                style={{ width: '40px', height: '40px' }}
                              >
                                <i className={`fas ${viewingDepartment.status ? 'fa-check' : 'fa-times'} text-white`}></i>
                              </div>
                              <h6 className="fw-bold mb-1">
                                {viewingDepartment.status ? 'Active Department' : 'Inactive Department'}
                              </h6>
                              <small className="text-muted">
                                {viewingDepartment.status 
                                  ? 'Currently accepting new students' 
                                  : 'Not accepting new students'
                                }
                              </small>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="border-top pt-3">
                            <h6 className="fw-bold mb-3">Department Info</h6>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-muted">Department ID:</span>
                              <span className="fw-medium">#{viewingDepartment.id}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-muted">Faculty ID:</span>
                              <span className="fw-medium">#{viewingDepartment.facultyId}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted">Campus ID:</span>
                              <span className="fw-medium">#{viewingDepartment.campusId}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      handleCloseView();
                      handleEdit(viewingDepartment);
                    }}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Edit Department
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseView}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Departments List */}
        <DepartmentList
          onEdit={handleEdit}
          onView={handleView}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Departments;